import { useLocale } from 'next-intl';
import { type Locale, localeRegex, locales, nonLatinLocales } from "@/constants/locales"
import { Routes } from "@/constants/routes"
import { RouteId } from "@/types/routes";

export function getLocalePath(path: string, locale: string): string {
    // Remove any leading slash and locale prefix if present
    const cleanPath = path.replace(/^\//, '').replace(localeRegex, '');
    return `/${locale}/${cleanPath}`;
}

export function getRoute(
    href: string,
    locale: Locale,
    options?: { fullMatch?: boolean }
): [string, any] {
    const routeObj = Object.entries(Routes).find(([, route]) => {
        if (!route?.href) return false;
        if (!route.href[locale]) return false;

        const _href = decodeURIComponent(href);

        if (options?.fullMatch !== false) {
            return route.href[locale] === _href;
        }

        return route.href[locale].replace(`/${locale}/`, '') === _href;
    });

    return routeObj as [string, any] || [null, null];
}

/**
 * Gets the route object from a URL path
 * @param url The URL path to get the route for
 * @param locale The locale to use for the route
 * @returns The route object or undefined if not found
 */
export function getRouteFromUrl(url: string, locale: Locale) {
    // Remove locale prefix if present
    const pathWithoutLocale = url.replace(`/${locale}/`, '');
    const isNonLatinLang = nonLatinLocales.includes(locale as never);
    const pathName = isNonLatinLang ? decodeURIComponent(pathWithoutLocale) : pathWithoutLocale;

    // Find the route that matches this path
    const routeEntry = Object.entries(Routes).find(([, route]) => {
        return route.href[locale].replace(`/${locale}/`, '') === pathName;
    });

    return routeEntry ? { key: routeEntry[0], route: routeEntry[1] } : undefined;
}

export function getRouteByCategoryId(categoryId: string, locale: Locale) {
    const route = Object.entries(Routes).find(([routeId]) => routeId === categoryId);

    return route ? {
        key: route[0],
        route: route[1],
        link: route[1].href[locale]
    } : {
        key: '',
        route: null,
        link: ''
    }
}

export function getNavigationPath(path: RouteId, locale: Locale): string {
    const route = Object.entries(Routes).find(([routeId]) => routeId === path);
    return route ? route[1].href[locale] : '';
}

/**
 * Checks if a route is currently active based on the current pathname
 * @param routeKey The route key from Routes
 * @param currentPathname The current pathname
 * @param locale The current locale
 * @returns True if the route is active
 */
export function isRouteActive(routeKey: string, currentPathname: string, locale: Locale): boolean {
    const route = Routes[routeKey as keyof typeof Routes];
    if (!route || !route.href[locale]) return false;

    const routePath = route.href[locale];

    // Handle exact matches
    if (currentPathname === routePath) return true;

    // For home route, only match exact path to avoid matching all routes
    if (routeKey === 'home') {
        return currentPathname === routePath;
    }

    // For other routes, check if current path starts with route path
    return currentPathname.startsWith(routePath + '/') || currentPathname === routePath;
}

export function useLocaleNavigation() {
    const locale = useLocale();

    return {
        getPath: (path: string) => getLocalePath(path, locale),
        getNavigationPath: (path: RouteId) => getNavigationPath(path, locale as Locale),
        isRouteActive: (routeKey: string, currentPathname: string) =>
            isRouteActive(routeKey, currentPathname, locale as Locale),
    };
}

// Function to find alternate paths for a given path and language
function findAlternatePaths(path: string, currentLang: Locale) {
    const alternates: { lang: Locale, path: string }[] = [];

    for (const key in Routes) {
        const route = Routes[key as keyof typeof Routes];
        const currentPath = route.href[currentLang];

        // If we found a matching path
        if (currentPath === `/${currentLang}${path ? `/${path}` : ''}`) {
            // Add all other language versions as alternates
            for (const lang of locales) {
                if (lang !== currentLang && route.href[lang]) {
                    alternates.push({
                        lang: lang,
                        path: (route.href[lang] as string).substring(1) // Remove leading slash
                    });
                }
            }
            break;
        }
    }

    return alternates;
}


export function getAlternateLinks(pathname: string) {
    const cleanPath = pathname.startsWith('/') ? pathname.substring(1) : pathname;
    const langCode = cleanPath.split('/')[0] as Locale;

    const isNonLatinLang = nonLatinLocales.includes(langCode as never);
    const pathName = isNonLatinLang ? decodeURIComponent(cleanPath) : cleanPath;

    if (locales.includes(langCode)) {
        const pathWithoutLang = pathName.substring(langCode.length + 1); // Remove 'lang/' prefix

        // Find all alternate language paths
        const alternatePaths = findAlternatePaths(pathWithoutLang, langCode);

        // Return the alternate paths found
        if (alternatePaths.length > 0) {
            return alternatePaths;
        }
    }

    return [];
}